import * as React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The campaign wizard's header. Every completed step stays clickable — going
 * back to change the budget must not mean starting the brief again.
 *
 * Forward steps are deliberately not clickable: the wizard's later steps read
 * from earlier ones (the creator shortlist is matched against the brief), so
 * skipping ahead would show a screen built on nothing.
 */
export interface StepperStep {
  id: string;
  label: React.ReactNode;
}

export interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: StepperStep[];
  current: number;
  onStep?: (index: number) => void;
}

export function Stepper({ steps, current, onStep, className, ...props }: StepperProps) {
  return (
    <div className={cn("flex items-start", className)} {...props}>
      {steps.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const reachable = i <= current;
        const isLast = i === steps.length - 1;

        return (
          <div
            key={step.id}
            className={cn("flex min-w-0 items-start", isLast ? "flex-none" : "flex-1")}
          >
            <button
              type="button"
              disabled={!reachable}
              aria-current={active ? "step" : undefined}
              onClick={() => reachable && onStep?.(i)}
              className={cn(
                "flex w-26 shrink-0 flex-col items-center gap-2",
                reachable ? "cursor-pointer" : "cursor-default",
              )}
            >
              <span
                className={cn(
                  "inline-flex size-[26px] items-center justify-center rounded-full border-[1.5px] text-fine font-bold",
                  "transition-[background-color,border-color,color] duration-[400ms] ease-[cubic-bezier(.2,.8,.2,1)]",
                  done && "border-steel-600 bg-steel-600 text-white",
                  active && "border-steel-600 bg-steel-50 text-steel-600",
                  !done && !active && "border-neutral-200 bg-transparent text-neutral-400",
                )}
              >
                {done ? <Check className="size-[13px]" strokeWidth={3} /> : i + 1}
              </span>
              <span
                className={cn(
                  "text-fine leading-tight",
                  active ? "font-bold" : "font-medium",
                  done || active ? "text-neutral-900" : "text-neutral-400",
                )}
              >
                {step.label}
              </span>
            </button>

            {!isLast && (
              <span className="relative mt-[13px] h-[1.5px] min-w-4 flex-1 overflow-hidden bg-neutral-200">
                <span
                  className="absolute inset-0 origin-left bg-steel-600 transition-transform duration-500"
                  style={{ transform: `scaleX(${done ? 1 : 0})` }}
                />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
